/*
 * InSight Analytics Solutions Limited
 * https://fieldpro.onyxinsight.com/
 *
 * Copyright (c) 2021 Onyx InSight Field Pro.
 * This file is licensed to InSight Analytics Solutions Limited
 */
/* NPM Imports Start */
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import PropsTypes from 'prop-types';
import Button from 'devextreme-react/button';
import Drawer from 'devextreme-react/drawer';
import Cookies from 'universal-cookie';
import { useSelector, useDispatch } from 'react-redux';
import {
  sixZero,
  dxdrawerpanelcontent,
  headerComponent,
} from './../../constant';
/* NPM Imports End */

/* Project Imports Start */
import { Header, SideNavigationMenu, Footer } from '../../components';
import { useScreenSize } from '../../utils/media-query';
import { Template } from 'devextreme-react/core/template';
import { useMenuPatch } from '../../utils/patches';
import logo from '../../assets/images/FieldPRO_Logo_Colour.svg';
import { ReactSVG } from 'react-svg';
import {
  getLandingPage,
  getConfiguration,
  storeLoginUrl,
} from '../../store/action/configurationAction';

/* Project Imports End */

/* CCS & Image imports Start */
import './side-nav-inner-toolbar.scss';
/* CCS & Image imports End */

const SideNavInnerToolbar = function({ children }) {
  const history = useHistory();
  const user = useSelector(state => state.configuration.user);
  const fromLogin = useSelector(state => state.configuration.fromLogin);
  const location = useLocation();
  const cookies = new Cookies();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (
      fromLogin === true &&
      location.pathname !== '/CustomerList' &&
      cookies.get('token') &&
      cookies.get('linkNavigationUrl')
    ) {
      window.location.replace(cookies.get('linkNavigationUrl', '_self'));
      setIsOpen(true);
    }
    if (
      (cookies.get('token') && location.pathname === '/') ||
      (cookies.get('token') && location.pathname === '#/') ||
      (cookies.get('token') && location.pathname === '/login')
    ) {
      if (!user) {
        dispatch(getConfiguration());
      }
      if (user) {
        history.push(`/${user.replace('#', '')}`);
      }
    }
    if (
      cookies.get('token') &&
      location.pathname === '/login' &&
      cookies.get('userdetails').customers[0].authLevel === 'Super Admin'
    ) {
      history.push('/CustomerList');
    }
    if (!cookies.get('token') && location.pathname === '/login') {
      document.querySelector(dxdrawerpanelcontent).classList.add('hide');
      document.querySelector(headerComponent).classList.add('hide');
      if (cookies.get('token')) {
        dispatch(getLandingPage());
      }
    } else {
      document.querySelector(dxdrawerpanelcontent).classList.remove('hide');
      document.querySelector(headerComponent).classList.remove('hide');
    }
    if (
      location.pathname.includes('/reportPage/') ||
      location.pathname.includes('/reportViewerPage')
    ) {
      document.querySelector(dxdrawerpanelcontent).classList.add('hide');
      document.querySelector(headerComponent).classList.add('hide');
    }
    if (isOpen === true) {
      cookies.remove('linkNavigationUrl', { path: '/' });
      dispatch(storeLoginUrl(false));
    }
  }, [user, location]);
  const { isXSmall, isLarge, isSmall, isMedium } = useScreenSize();
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState(
    isLarge || isSmall || isMedium ? MenuStatus.Opened : MenuStatus.Closed,
  );

  const temporaryOpenMenu = useCallback(() => {
    setMenuStatus(prevMenuStatus =>
      prevMenuStatus === MenuStatus.Closed
        ? MenuStatus.TemporaryOpened
        : prevMenuStatus,
    );
  }, []);

  const onNavigationChanged = useCallback(
    ({ itemData: { path }, event, node }) => {
      if (menuStatus === MenuStatus.Closed || !path || node.selected) {
        event.preventDefault();
        return;
      }
      history.push(path);
      if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
        setMenuStatus(MenuStatus.Closed);
        event.stopPropagation();
      }
    },
    [history, menuStatus, isLarge],
  );
  const togglesidebar = () => {
    document
      .querySelector(dxdrawerpanelcontent)
      .classList.toggle('togglesidebar');
    const elems = document
      .querySelector('.beforesiderbar')
      .querySelectorAll('.sidenavhead');
    const i = document.querySelectorAll('.beforesiderbar span');
    const imgwidth = document.querySelectorAll('.beforesiderbar img');
    [].forEach.call(elems, function(el) {
      el.classList.toggle('hide');
    });
    [].forEach.call(i, function(el) {
      el.classList.toggle('hide');
    });
    [].forEach.call(imgwidth, function(el) {
      el.classList.toggle('sidenavatagimgtoggle');
    });
  };

  return (
    <div className={'side-nav-inner-toolbar'}>
      <Drawer
        className={['drawer', patchCssClass].join(' ')}
        position={'before'}
        openedStateMode={isLarge || isSmall || isMedium ? 'shrink' : 'overlap'}
        revealMode={isXSmall ? 'slide' : 'expand'}
        minSize={isXSmall ? 0 : sixZero}
        maxSize={250}
        shading={isLarge || isSmall || isMedium ? false : true}
        opened={menuStatus === MenuStatus.Closed ? false : true}
        template={'menu'}>
        <div className={'container'}>
          <Header />
          <div className={'content'}>
            {React.Children.map(children, item => item.type !== Footer && item)}
          </div>
          <div className={'content-block'}>
            {React.Children.map(children, item => item.type === Footer && item)}
          </div>
        </div>
        <Template name={'menu'}>
          <SideNavigationMenu
            compactMode={menuStatus === MenuStatus.Closed}
            selectedItemChanged={onNavigationChanged}
            openMenu={temporaryOpenMenu}
            onMenuReady={onMenuReady}
            className="sidebarmenu">
            <div className="logoSection">
              <div className="logo">
                <ReactSVG style={{ width: '180px' }} src={logo} />
              </div>
              <div className="btnMenu">
                <Button
                  icon="menu"
                  elementAttr={{ 'data-cy': 'RSM-Menu-Button' }}
                  stylingMode="text"
                  className=""
                  onClick={togglesidebar}
                />
              </div>
            </div>
          </SideNavigationMenu>
        </Template>
      </Drawer>
    </div>
  );
};

const MenuStatus = {
  Closed: 1,
  Opened: 2,
  TemporaryOpened: 3,
};

export default SideNavInnerToolbar;

SideNavInnerToolbar.displayName = 'SideNavInnerToolbar';

/* PropsTypes Validation Start*/
SideNavInnerToolbar.propTypes = {
  children: PropsTypes.any,
};
/* PropsTypes Validation End*/

/* DefaultProps */
SideNavInnerToolbar.defaultProps = {};
/* DefaultProps */
